import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AUDIO_SOURCE_VALUES,
  BANNER_KIND_VALUES,
  BANNER_POSITION_VALUES,
  BANNER_PURPOSE_VALUES,
  type AudioSource,
  type BannerKind,
  type BannerPosition,
  type BannerPurpose,
  type BannerStatus,
} from '@content-engine/shared';
import { BannerCropEditor } from '../components/BannerCropEditor.tsx';
import { ApiError } from '../lib/api.ts';
import {
  fetchBanner,
  fetchBanners,
  updateBannerApi,
  uploadBannerApi,
  uploadBannerFromUrlApi,
  type BannerUploadParams,
} from '../lib/queries.ts';
import type { Banner } from '../lib/types.ts';

export function BannerLibraryPage(): React.ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<BannerStatus | 'all'>('active');
  const [kindFilter, setKindFilter] = useState<BannerKind | ''>('');
  const [tagFilter, setTagFilter] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [editing, setEditing] = useState<Banner | null>(null);

  const bannersQuery = useQuery({
    queryKey: ['banners', statusFilter, kindFilter, tagFilter],
    queryFn: () =>
      fetchBanners({
        status: statusFilter,
        ...(kindFilter ? { kind: kindFilter } : {}),
        ...(tagFilter ? { tag: tagFilter } : {}),
      }),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => updateBannerApi(id, { status: 'archived' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['banners'] }),
  });
  const unarchiveMutation = useMutation({
    mutationFn: (id: string) => updateBannerApi(id, { status: 'active' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['banners'] }),
  });

  const banners = bannersQuery.data?.banners ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('banners.title')}</h1>
          <div className="sub">{t('banners.subtitle')}</div>
        </div>
        <div className="filters">
          <button className="primary" onClick={() => setShowUpload(true)}>
            {t('banners.uploadButton')}
          </button>
        </div>
      </div>

      <div className="section">
        <div className="section-head" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <h2 style={{ marginRight: 'auto' }}>
            {t('banners.all')} <span className="count">{banners.length}</span>
          </h2>
          <label style={{ fontSize: 11, color: 'var(--text-3)' }}>{t('banners.filterStatus')}:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as BannerStatus | 'all')}
            style={{ fontSize: 12 }}
          >
            <option value="active">{t('banners.status_active')}</option>
            <option value="archived">{t('banners.status_archived')}</option>
            <option value="all">{t('common.all')}</option>
          </select>
          <label style={{ fontSize: 11, color: 'var(--text-3)' }}>{t('banners.filterKind')}:</label>
          <select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as BannerKind | '')}
            style={{ fontSize: 12 }}
          >
            <option value="">{t('common.all')}</option>
            {BANNER_KIND_VALUES.map((k) => (
              <option key={k} value={k}>
                {t(`banners.kind_${k}`)}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder={t('banners.filterTag')}
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            style={{ fontSize: 12, width: 120 }}
          />
        </div>
        <div className="section-body">
          {bannersQuery.isLoading && <div>{t('common.loading')}</div>}
          {banners.length === 0 && !bannersQuery.isLoading && (
            <div style={{ padding: 24, color: 'var(--text-3)' }}>{t('banners.noBanners')}</div>
          )}
          {banners.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 16,
              }}
            >
              {banners.map((b) => (
                <BannerCard
                  key={b.id}
                  banner={b}
                  onArchive={() => {
                    if (confirm(t('banners.archiveConfirm'))) archiveMutation.mutate(b.id);
                  }}
                  onUnarchive={() => unarchiveMutation.mutate(b.id)}
                  onEdit={() => setEditing(b)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {showUpload && (
        <UploadBannerModal
          onClose={() => setShowUpload(false)}
          onUploaded={() => {
            setShowUpload(false);
            void queryClient.invalidateQueries({ queryKey: ['banners'] });
          }}
        />
      )}
      {editing && (
        <EditBannerModal
          banner={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['banners'] });
          }}
        />
      )}
    </>
  );
}

function BannerCard({
  banner,
  onArchive,
  onUnarchive,
  onEdit,
}: {
  banner: Banner;
  onArchive: () => void;
  onUnarchive: () => void;
  onEdit: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const detailQuery = useQuery({
    queryKey: ['banner', banner.id],
    queryFn: () => fetchBanner(banner.id),
  });
  const url = detailQuery.data?.presignedUrl;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  function togglePlay(): void {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      // Honour browser autoplay rules: user gesture lets us unmute before play.
      // If the file has no audio track this is a no-op.
      v.muted = false;
      void v.play();
    } else {
      v.pause();
    }
  }

  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 8,
        background: 'var(--bg-elev)',
        overflow: 'hidden',
      }}
    >
      <div style={{ aspectRatio: '9 / 16', background: '#000', position: 'relative' }}>
        {url ? (
          banner.kind === 'image' ? (
            <img
              crossOrigin="anonymous"
              src={url}
              alt={banner.name}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            <>
              <video
                ref={videoRef}
                crossOrigin="anonymous"
                src={url}
                preload="metadata"
                muted={!isPlaying}
                playsInline
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                onClick={togglePlay}
                style={{ width: '100%', height: '100%', objectFit: 'contain', cursor: 'pointer' }}
              />
              {!isPlaying && (
                <button
                  type="button"
                  onClick={togglePlay}
                  aria-label={t('banners.playButton')}
                  title={t('banners.playButton')}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0, 0, 0, 0.25)',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#fff',
                    fontSize: 42,
                    padding: 0,
                  }}
                >
                  <span
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      background: 'rgba(0, 0, 0, 0.55)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      paddingLeft: 6,
                    }}
                  >
                    ▶
                  </span>
                </button>
              )}
            </>
          )
        ) : null}
      </div>
      <div style={{ padding: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <strong style={{ fontSize: 14 }}>{banner.name}</strong>
          <span className={`pill ${banner.status === 'active' ? 'live' : ''}`}>
            {t(`banners.status_${banner.status}`)}
          </span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
          {t(`banners.kind_${banner.kind}`)}
          {' · '}
          <span style={{ color: banner.purpose === 'background' ? 'var(--neon)' : 'var(--text-3)' }}>
            {t(`banners.purpose_${banner.purpose}`)}
          </span>
          {banner.purpose === 'banner' && ` · ${banner.defaultPosition} · ${banner.defaultSizePct}%`}
        </div>
        {banner.tags.length > 0 && (
          <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {banner.tags.map((tag) => (
              <span
                key={tag}
                style={{
                  fontSize: 10,
                  background: 'var(--bg-soft)',
                  padding: '2px 6px',
                  borderRadius: 3,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
          <button onClick={onEdit} style={{ fontSize: 11 }}>
            {t('banners.edit')}
          </button>
          {banner.status === 'active' ? (
            <button onClick={onArchive} style={{ fontSize: 11 }}>
              {t('banners.archive')}
            </button>
          ) : (
            <button onClick={onUnarchive} style={{ fontSize: 11 }}>
              {t('banners.unarchive')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function UploadBannerModal({
  onClose,
  onUploaded,
}: {
  onClose: () => void;
  onUploaded: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const [source, setSource] = useState<'file' | 'url'>('file');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [tags, setTags] = useState('');
  const [purpose, setPurpose] = useState<BannerPurpose>('banner');
  const [defaultPosition, setDefaultPosition] = useState<BannerPosition>('bottom-right');
  const [defaultSizePct, setDefaultSizePct] = useState(20);
  const [defaultLoop, setDefaultLoop] = useState(true);
  const [useChroma, setUseChroma] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [chromaColor, setChromaColor] = useState('0x00B140');
  const [chromaSimilarity, setChromaSimilarity] = useState(0.15);
  const [chromaBlend, setChromaBlend] = useState(0.05);
  const [cropTop, setCropTop] = useState(0);
  const [cropRight, setCropRight] = useState(0);
  const [cropBottom, setCropBottom] = useState(0);
  const [cropLeft, setCropLeft] = useState(0);
  const [audioSource, setAudioSource] = useState<AudioSource>('clip');
  const [notes, setNotes] = useState('');

  // For URL sources we always assume video (yt-dlp produces mp4).
  const isVideo = source === 'url' ? true : (file?.type.startsWith('video/') ?? false);
  const fileKind: 'image' | 'video' | null = file?.type.startsWith('image/')
    ? 'image'
    : file?.type.startsWith('video/')
      ? 'video'
      : null;

  // Create a local preview URL for the selected file; revoke on swap/close.
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setFilePreviewUrl(null);
      return;
    }
    const u = URL.createObjectURL(file);
    setFilePreviewUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const mutation = useMutation({
    mutationFn: () => {
      const params: BannerUploadParams = {
        name,
        purpose,
        tags: tags
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        defaultPosition,
        defaultSizePct,
        ...(isVideo
          ? {
              defaultLoop,
              ...(useChroma
                ? {
                    defaultChromaColor: chromaColor,
                    defaultChromaSimilarity: chromaSimilarity,
                    defaultChromaBlend: chromaBlend,
                  }
                : { defaultChromaColor: undefined }),
            }
          : {}),
        ...(cropTop > 0 ? { defaultCropTopPct: cropTop } : {}),
        ...(cropRight > 0 ? { defaultCropRightPct: cropRight } : {}),
        ...(cropBottom > 0 ? { defaultCropBottomPct: cropBottom } : {}),
        ...(cropLeft > 0 ? { defaultCropLeftPct: cropLeft } : {}),
        defaultAudioSource: audioSource,
        ...(notes ? { notes } : {}),
      };
      if (source === 'url') {
        if (!url.trim()) throw new Error('No URL');
        return uploadBannerFromUrlApi(url.trim(), params);
      }
      if (!file) throw new Error('No file');
      return uploadBannerApi(file, params);
    },
    onSuccess: () => onUploaded(),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    mutation.mutate();
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 540,
          background: 'var(--bg-card)',
          border: '1px solid var(--line)',
          borderRadius: 12,
          padding: 28,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 18, marginBottom: 16 }}>{t('banners.uploadModal.title')}</h2>
        <form onSubmit={submit}>
          <div className="form-row" style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
              <input
                type="radio"
                name="banner-source"
                checked={source === 'file'}
                onChange={() => setSource('file')}
              />
              {t('banners.uploadModal.sourceFile')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
              <input
                type="radio"
                name="banner-source"
                checked={source === 'url'}
                onChange={() => setSource('url')}
              />
              {t('banners.uploadModal.sourceUrl')}
            </label>
          </div>
          {source === 'file' ? (
            <div className="form-row">
              <label>{t('banners.uploadModal.fileLabel')}</label>
              <input
                type="file"
                accept="image/*,video/*"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                required
              />
            </div>
          ) : (
            <div className="form-row">
              <label>{t('banners.uploadModal.urlLabel')}</label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://youtube.com/shorts/..."
                required
              />
              <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 4 }}>
                {t('banners.uploadModal.urlHint')}
              </div>
            </div>
          )}
          <div className="form-row">
            <label>{t('banners.uploadModal.nameLabel')}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="form-row">
            <label>{t('banners.uploadModal.tagsLabel')}</label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder={t('banners.uploadModal.tagsPlaceholder')}
            />
          </div>
          <div className="form-row">
            <label>{t('banners.purposeLabel')}</label>
            <select value={purpose} onChange={(e) => setPurpose(e.target.value as BannerPurpose)}>
              {BANNER_PURPOSE_VALUES.map((p) => (
                <option key={p} value={p}>
                  {t(`banners.purpose_${p}`)}
                </option>
              ))}
            </select>
          </div>
          {purpose === 'banner' && (
            <div className="form-row">
              <label>{t('banners.uploadModal.defaultPositionLabel')}</label>
              <select
                value={defaultPosition}
                onChange={(e) => setDefaultPosition(e.target.value as BannerPosition)}
              >
                {BANNER_POSITION_VALUES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="form-row">
            <label>{t('banners.uploadModal.defaultSizeLabel', { value: defaultSizePct })}</label>
            <input
              type="range"
              min={10}
              max={100}
              value={defaultSizePct}
              onChange={(e) => setDefaultSizePct(Number(e.target.value))}
            />
          </div>
          {isVideo && (
            <>
              <div className="form-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  id="dloop"
                  checked={defaultLoop}
                  onChange={(e) => setDefaultLoop(e.target.checked)}
                />
                <label htmlFor="dloop" style={{ margin: 0 }}>
                  {t('banners.uploadModal.defaultLoopLabel')}
                </label>
              </div>
              <div className="form-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  id="duchroma"
                  checked={useChroma}
                  onChange={(e) => setUseChroma(e.target.checked)}
                />
                <label htmlFor="duchroma" style={{ margin: 0 }}>
                  {t('banners.uploadModal.useChromaLabel')}
                </label>
              </div>
              {useChroma && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    style={{ fontSize: 11, marginBottom: 8 }}
                  >
                    {t('banners.uploadModal.advancedToggle')} {showAdvanced ? '▴' : '▾'}
                  </button>
                  {showAdvanced && (
                    <>
                      <div className="form-row">
                        <label>{t('banners.uploadModal.chromaColorLabel')}</label>
                        <input
                          type="text"
                          value={chromaColor}
                          onChange={(e) => setChromaColor(e.target.value)}
                          style={{ fontFamily: 'var(--mono)' }}
                        />
                      </div>
                      <div className="form-row">
                        <label>
                          {t('banners.uploadModal.chromaSimilarityLabel')}: {chromaSimilarity.toFixed(2)}
                        </label>
                        <input
                          type="range"
                          min={0.01}
                          max={0.5}
                          step={0.01}
                          value={chromaSimilarity}
                          onChange={(e) => setChromaSimilarity(Number(e.target.value))}
                        />
                      </div>
                      <div className="form-row">
                        <label>
                          {t('banners.uploadModal.chromaBlendLabel')}: {chromaBlend.toFixed(2)}
                        </label>
                        <input
                          type="range"
                          min={0}
                          max={0.3}
                          step={0.01}
                          value={chromaBlend}
                          onChange={(e) => setChromaBlend(Number(e.target.value))}
                        />
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}
          <div className="form-row">
            <label>{t('banners.uploadModal.cropLabel')}</label>
            {source === 'file' && filePreviewUrl && fileKind ? (
              <BannerCropEditor
                mediaUrl={filePreviewUrl}
                mediaKind={fileKind}
                crop={{ top: cropTop, right: cropRight, bottom: cropBottom, left: cropLeft }}
                onChange={(c) => {
                  setCropTop(c.top);
                  setCropRight(c.right);
                  setCropBottom(c.bottom);
                  setCropLeft(c.left);
                }}
              />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                <CropInput label={t('banners.uploadModal.cropTop')} value={cropTop} onChange={setCropTop} />
                <CropInput label={t('banners.uploadModal.cropRight')} value={cropRight} onChange={setCropRight} />
                <CropInput label={t('banners.uploadModal.cropBottom')} value={cropBottom} onChange={setCropBottom} />
                <CropInput label={t('banners.uploadModal.cropLeft')} value={cropLeft} onChange={setCropLeft} />
              </div>
            )}
          </div>
          {isVideo && (
            <div className="form-row">
              <label>{t('banners.uploadModal.audioSourceLabel')}</label>
              <select value={audioSource} onChange={(e) => setAudioSource(e.target.value as AudioSource)}>
                {AUDIO_SOURCE_VALUES.map((v) => (
                  <option key={v} value={v}>{t(`banners.uploadModal.audioSource_${v}`)}</option>
                ))}
              </select>
            </div>
          )}
          <div className="form-row">
            <label>{t('banners.uploadModal.notesLabel')}</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
          {mutation.error && (
            <div style={{ color: 'var(--hot)', fontSize: 12, marginBottom: 12 }}>
              {(mutation.error as Error).message}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="primary"
              disabled={
                !name ||
                mutation.isPending ||
                (source === 'file' ? !file : !url.trim())
              }
            >
              {mutation.isPending
                ? t('banners.uploadModal.submitting')
                : t('banners.uploadModal.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditBannerModal({
  banner,
  onClose,
  onSaved,
}: {
  banner: Banner;
  onClose: () => void;
  onSaved: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const [name, setName] = useState(banner.name);
  const [tags, setTags] = useState(banner.tags.join(', '));
  const [purpose, setPurpose] = useState<BannerPurpose>(banner.purpose);
  const [defaultPosition, setDefaultPosition] = useState<BannerPosition>(banner.defaultPosition);
  const [defaultSizePct, setDefaultSizePct] = useState(banner.defaultSizePct);
  const [cropTop, setCropTop] = useState(Number(banner.defaultCropTopPct ?? '0'));
  const [cropRight, setCropRight] = useState(Number(banner.defaultCropRightPct ?? '0'));
  const [cropBottom, setCropBottom] = useState(Number(banner.defaultCropBottomPct ?? '0'));
  const [cropLeft, setCropLeft] = useState(Number(banner.defaultCropLeftPct ?? '0'));
  const [audioSource, setAudioSource] = useState<AudioSource>(banner.defaultAudioSource ?? 'clip');
  const [notes, setNotes] = useState(banner.notes ?? '');

  const detailQuery = useQuery({
    queryKey: ['banner', banner.id],
    queryFn: () => fetchBanner(banner.id),
  });
  const mediaUrl = detailQuery.data?.presignedUrl;

  const mutation = useMutation({
    mutationFn: () =>
      updateBannerApi(banner.id, {
        name,
        purpose,
        tags: tags
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        defaultPosition,
        defaultSizePct,
        defaultCropTopPct: cropTop,
        defaultCropRightPct: cropRight,
        defaultCropBottomPct: cropBottom,
        defaultCropLeftPct: cropLeft,
        defaultAudioSource: audioSource,
        notes: notes.trim() ? notes : null,
      }),
    onSuccess: () => onSaved(),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    mutation.mutate();
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 480,
          background: 'var(--bg-card)',
          border: '1px solid var(--line)',
          borderRadius: 12,
          padding: 28,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 18, marginBottom: 16 }}>{t('banners.editModal.title')}</h2>
        <form onSubmit={submit}>
          <div className="form-row">
            <label>{t('banners.uploadModal.nameLabel')}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="form-row">
            <label>{t('banners.uploadModal.tagsLabel')}</label>
            <input type="text" value={tags} onChange={(e) => setTags(e.target.value)} />
          </div>
          <div className="form-row">
            <label>{t('banners.purposeLabel')}</label>
            <select value={purpose} onChange={(e) => setPurpose(e.target.value as BannerPurpose)}>
              {BANNER_PURPOSE_VALUES.map((p) => (
                <option key={p} value={p}>
                  {t(`banners.purpose_${p}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>{t('banners.uploadModal.defaultPositionLabel')}</label>
            <select
              value={defaultPosition}
              onChange={(e) => setDefaultPosition(e.target.value as BannerPosition)}
            >
              {BANNER_POSITION_VALUES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>{t('banners.uploadModal.defaultSizeLabel', { value: defaultSizePct })}</label>
            <input
              type="range"
              min={10}
              max={100}
              value={defaultSizePct}
              onChange={(e) => setDefaultSizePct(Number(e.target.value))}
            />
          </div>
          <div className="form-row">
            <label>{t('banners.uploadModal.cropLabel')}</label>
            {mediaUrl ? (
              <BannerCropEditor
                mediaUrl={mediaUrl}
                mediaKind={banner.kind}
                crop={{ top: cropTop, right: cropRight, bottom: cropBottom, left: cropLeft }}
                onChange={(c) => {
                  setCropTop(c.top);
                  setCropRight(c.right);
                  setCropBottom(c.bottom);
                  setCropLeft(c.left);
                }}
              />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                <CropInput label={t('banners.uploadModal.cropTop')} value={cropTop} onChange={setCropTop} />
                <CropInput label={t('banners.uploadModal.cropRight')} value={cropRight} onChange={setCropRight} />
                <CropInput label={t('banners.uploadModal.cropBottom')} value={cropBottom} onChange={setCropBottom} />
                <CropInput label={t('banners.uploadModal.cropLeft')} value={cropLeft} onChange={setCropLeft} />
              </div>
            )}
          </div>
          {banner.kind === 'video' && (
            <div className="form-row">
              <label>{t('banners.uploadModal.audioSourceLabel')}</label>
              <select value={audioSource} onChange={(e) => setAudioSource(e.target.value as AudioSource)}>
                {AUDIO_SOURCE_VALUES.map((v) => (
                  <option key={v} value={v}>{t(`banners.uploadModal.audioSource_${v}`)}</option>
                ))}
              </select>
            </div>
          )}
          <div className="form-row">
            <label>{t('banners.uploadModal.notesLabel')}</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
          {mutation.error instanceof ApiError && (
            <div style={{ color: 'var(--hot)', fontSize: 12, marginBottom: 12 }}>
              {mutation.error.message}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="primary" disabled={mutation.isPending}>
              {mutation.isPending
                ? t('banners.editModal.submitting')
                : t('banners.editModal.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CropInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
}): React.ReactElement {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 10, color: 'var(--text-3)' }}>
      <span>{label} %</span>
      <input
        type="number"
        min={0}
        max={95}
        step={1}
        value={value}
        onChange={(e) => {
          const n = Number(e.target.value);
          onChange(Number.isFinite(n) ? Math.max(0, Math.min(95, n)) : 0);
        }}
        style={{ fontSize: 12, padding: '2px 4px' }}
      />
    </label>
  );
}
